import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { SEO } from '@/components/SEO';
import { MediaPlayer } from '@/components/media/MediaPlayer';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { BackButton } from '@/components/BackButton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Heart, MessageCircle, Eye, Share2, Play } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { MentionInput } from '@/components/mentions/MentionInput';
import { MentionText } from '@/components/mentions/MentionText';

interface Comment {
  id: string;
  comment: string;
  created_at: string;
  user_id: string;
  profiles: {
    username: string;
    display_name: string;
    avatar_url: string;
  };
}

export default function WatchRecording() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [recording, setRecording] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [liked, setLiked] = useState(false);
  const [likesCount, setLikesCount] = useState(0);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [moreRecordings, setMoreRecordings] = useState<any[]>([]);

  useEffect(() => {
    if (id) {
      loadRecording();
      loadComments();
      incrementViewCount();
    }
  }, [id]);

  useEffect(() => {
    if (recording?.creator_id) {
      loadMoreFromCreator();
    }
  }, [recording?.creator_id]);

  const loadRecording = async () => {
    try {
      const { data, error } = await supabase
        .from('recordings')
        .select(`
          *,
          profiles!recordings_creator_id_fkey(username, display_name, avatar_url, is_verified)
        `)
        .eq('id', id)
        .single();

      if (error) throw error;

      setRecording(data);

      // Get likes count
      const { count } = await supabase
        .from('recording_likes')
        .select('*', { count: 'exact', head: true })
        .eq('recording_id', id);

      setLikesCount(count || 0);

      // Check if user liked
      if (user) {
        const { data: likeData } = await supabase
          .from('recording_likes')
          .select('id')
          .eq('recording_id', id)
          .eq('user_id', user.id)
          .maybeSingle();

        setLiked(!!likeData);
      }
    } catch (error) {
      console.error('Error loading recording:', error);
      toast.error('Failed to load recording');
    } finally {
      setLoading(false);
    }
  };

  const loadComments = async () => {
    try {
      const { data, error } = await supabase
        .from('recording_comments')
        .select(`
          *,
          profiles!recording_comments_user_id_fkey(username, display_name, avatar_url)
        `)
        .eq('recording_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error loading comments:', error);
    }
  };

  const loadMoreFromCreator = async () => {
    try {
      const { data, error } = await supabase
        .from('recordings')
        .select('*, profiles!recordings_creator_id_fkey(username, display_name)')
        .eq('creator_id', recording.creator_id)
        .neq('id', id)
        .order('created_at', { ascending: false })
        .limit(6);

      if (error) throw error;
      setMoreRecordings(data || []);
    } catch (error) {
      console.error('Error loading more recordings:', error);
    }
  };

  const incrementViewCount = async () => {
    try {
      await supabase
        .from('recordings')
        .update({ view_count: recording?.view_count + 1 || 1 })
        .eq('id', id);
    } catch (error) {
      console.error('Error incrementing view count:', error);
    }
  };

  const handleLike = async () => {
    if (!user) {
      toast.error('Please sign in to like');
      return;
    }

    try {
      if (liked) {
        await supabase
          .from('recording_likes')
          .delete()
          .eq('recording_id', id)
          .eq('user_id', user.id);
        setLiked(false);
        setLikesCount((prev) => prev - 1);
      } else {
        await supabase
          .from('recording_likes')
          .insert({ recording_id: id, user_id: user.id });
        setLiked(true);
        setLikesCount((prev) => prev + 1);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      toast.error('Failed to update like');
    }
  };

  const handleSubmitComment = async () => {
    if (!user) {
      toast.error('Please sign in to comment');
      return;
    }

    if (!newComment.trim()) return;

    try {
      setSubmitting(true);
      const { error } = await supabase
        .from('recording_comments')
        .insert({
          recording_id: id,
          user_id: user.id,
          comment: newComment.trim(),
        });

      if (error) throw error;

      setNewComment('');
      loadComments();
      toast.success('Comment posted');
    } catch (error) {
      console.error('Error posting comment:', error);
      toast.error('Failed to post comment');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (!recording) {
    return (
      <div className="container mx-auto px-4 py-12 text-center">
        <h2 className="text-2xl font-bold mb-4">Recording not found</h2>
        <Link to="/discovery">
          <Button>Browse Recordings</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEO title={recording.title} description={recording.description} />

      <div className="container mx-auto px-4 py-6">
        <BackButton to="/" />
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="aspect-video bg-black rounded-lg overflow-hidden mb-4">
              <MediaPlayer src={recording.video_url} />
            </div>

            <h1 className="text-2xl font-bold mb-4">{recording.title}</h1>

            <div className="flex items-start justify-between mb-4">
              <Link to={`/profile/${recording.creator_id}`} className="flex items-center gap-3">
                <Avatar className="w-12 h-12">
                  <AvatarImage src={recording.profiles.avatar_url} />
                  <AvatarFallback>{recording.profiles.username[0]}</AvatarFallback>
                </Avatar>
                <div>
                  <div className="font-semibold flex items-center gap-2">
                    {recording.profiles.display_name || recording.profiles.username}
                    {recording.profiles.is_verified && (
                      <Badge variant="secondary">✓</Badge>
                    )}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {recording.view_count} views
                  </div>
                </div>
              </Link>

              <div className="flex gap-2">
                <Button
                  variant={liked ? 'default' : 'outline'}
                  size="sm"
                  onClick={handleLike}
                >
                  <Heart className={`w-4 h-4 mr-2 ${liked ? 'fill-current' : ''}`} />
                  {likesCount}
                </Button>
                <Button variant="outline" size="sm">
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>

            {recording.description && (
              <Card className="mb-6">
                <CardContent className="p-4">
                  <p className="whitespace-pre-wrap">{recording.description}</p>
                </CardContent>
              </Card>
            )}

            {/* Comments Section */}
            <div>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <MessageCircle className="w-5 h-5" />
                {comments.length} Comments
              </h2>

              {user && (
                <div className="mb-6">
                  <MentionInput
                    placeholder="Add a comment..."
                    value={newComment}
                    onChange={setNewComment}
                    multiline
                    className="mb-2"
                  />
                  <Button onClick={handleSubmitComment} disabled={submitting}>
                    Post Comment
                  </Button>
                </div>
              )}

              <div className="space-y-4">
                {comments.map((comment) => (
                  <Card key={comment.id}>
                    <CardContent className="p-4">
                      <div className="flex gap-3">
                        <Avatar className="w-10 h-10">
                          <AvatarImage src={comment.profiles.avatar_url} />
                          <AvatarFallback>{comment.profiles.username[0]}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1">
                          <div className="font-semibold text-sm">
                            {comment.profiles.display_name || comment.profiles.username}
                          </div>
                          <div className="text-sm text-muted-foreground mb-2">
                            {new Date(comment.created_at).toLocaleDateString()}
                          </div>
                          <MentionText text={comment.comment} className="text-sm" />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar - More from creator */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-4">
                <h3 className="font-semibold mb-4">More from this creator</h3>
                {moreRecordings.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No other recordings available
                  </p>
                ) : (
                  <div className="space-y-3">
                    {moreRecordings.map((rec) => (
                      <div
                        key={rec.id}
                        className="group cursor-pointer"
                        onClick={() => navigate(`/watch/${rec.id}`)}
                      >
                        <div className="aspect-video bg-muted rounded-lg mb-2 overflow-hidden relative">
                          {rec.thumbnail_url ? (
                            <img
                              src={rec.thumbnail_url}
                              alt={rec.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center">
                              <Play className="w-8 h-8 text-muted-foreground" />
                            </div>
                          )}
                          <div className="absolute bottom-1 right-1 bg-black/80 text-white text-xs px-1.5 py-0.5 rounded">
                            {Math.floor((rec.duration || 0) / 60)}:{String((rec.duration || 0) % 60).padStart(2, '0')}
                          </div>
                        </div>
                        <h4 className="text-sm font-medium line-clamp-2 group-hover:text-primary transition-colors">
                          {rec.title}
                        </h4>
                        <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                          <span>{rec.view_count || 0} views</span>
                          <span>•</span>
                          <span>{new Date(rec.created_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
